import { useState, useEffect, useCallback } from "react";
import {
  VIDEOS_API,
  VideoItem,
  VideoCategory,
  VideoCollection,
  VideosApiResponse,
  CatalogApiResponse,
} from "@/lib/videosApi";

export interface VideosData {
  videos: VideoItem[];
  categories: VideoCategory[];
  collections: VideoCollection[];
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

export function useVideosData(): VideosData {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [categories, setCategories] = useState<VideoCategory[]>([]);
  const [collections, setCollections] = useState<VideoCollection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [fetchTrigger, setFetchTrigger] = useState<number>(0);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(false);
    setFetchTrigger((c) => c + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(false);

    Promise.all([
      fetch(`${VIDEOS_API}/videos`).then((res) => {
        if (!res.ok) throw new Error("Failed to load videos");
        return res.json() as Promise<VideosApiResponse>;
      }),
      fetch(`${VIDEOS_API}/catalog`).then((res) => {
        if (!res.ok) throw new Error("Failed to load catalog");
        return res.json() as Promise<CatalogApiResponse>;
      }),
    ])
      .then(([videosRes, catalogRes]) => {
        if (!isMounted) return;
        setVideos(videosRes.videos || []);
        setCategories(catalogRes.categories || []);
        // Hide collections with lessonCount 0
        const validCollections = (catalogRes.collections || []).filter(
          (c) => c.lessonCount > 0,
        );
        setCollections(validCollections);
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error("Videos API error:", err);
        setError(true);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [fetchTrigger]);

  return { videos, categories, collections, loading, error, refetch };
}
